/**
 * The pick route: one explicit, hand-maintained ordering of everything in the freezer.
 *
 * Why this exists: packing slips used to be sorted on each product's own `purchased` counter.
 * That counter lives per shop, so a VCE slip and an HVO slip listed the same freezer in two
 * different orders - while there is only one freezer, laid out along VCE's popularity. Sorting
 * on a shared list instead makes both slips follow the same walking route, and gives the
 * order picking system (see planning/orderpicking-eigen-systeem.md) the route it needs.
 *
 * A route entry is a spot in the freezer. VCE and HVO sell the exact same physical product, so
 * one entry holds the product document from both shops and they share a single position.
 *
 * Everything in this file is pure: no Firestore, no React. The hook (data/hooks/usePickRoute)
 * does the reading and writing, this decides what the result should look like.
 *
 * Vakken per gewicht (planning/orderpicking-bouwplan-cms.md, besluit 2): every weight of a
 * product gets its own vak. An entry's product item is therefore `{ shopSource, productId, name,
 * weight }`, where `weight` is the normalised weight (the same spelling as in the article code)
 * or null for "all weights" - which is only right for a product that has no weights at all. Per
 * product it is either one "all weights" item or weight items only, never both: a pick line for
 * a weight without its own vak must go off-route, not fall back to the vak of another weight.
 */

import { articleBaseCode, articleCode, compareWeights, normaliseWeight, productKey as articleProductKey } from "./articleCode";

/** Gap between positions, so an entry can be dropped between two others without renumbering. */
export const SEQUENCE_STEP = 10;

export const SHOP_VCE = "VCE";
export const SHOP_HVO = "HVO";

/**
 * Name reduced to what two shops can agree on: no case, no accents, no punctuation.
 * Used to recognise "the same product" across the two Firestore projects, which share
 * no ids. Deliberately dumb - anything it misses is linked by hand in the CMS instead of
 * being guessed at here.
 */
export const normalizeProductName = (name) =>
  (name || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const productKey = articleProductKey;

/** The barcode on a vak's label: the vak code with a prefix no order or colli barcode can have. */
export const locationBarcode = (location) => (!location ? null : `LOC-${location}`);

/** Weight of an entry's product item, normalised. null = all weights of that product. */
export const itemWeight = (item) => normaliseWeight(item ? item.weight : null);

/** What the wave and the shelf label need to know about an entry, taken from it once. */
const spotOf = (entry) => ({
  entryId: entry.id || null,
  sequence: entry.sequence,
  location: entry.location || null,
  barcode: entry.barcode || locationBarcode(entry.location),
  zone: entry.zone || null,
  label: entry.label || "",
});

/**
 * Lookup structure for sorting. Built once per print run, then used per order line.
 *
 * Two ways in, in order of trust:
 *  - by product id + shop, which is exact
 *  - by normalized name, which catches lines whose product was replaced in the webshop
 *    but still sits on the route under the same name
 */
export const buildPickRouteIndex = (entries) => {
  const byProduct = new Map();
  const byName = new Map();
  // Article code -> the vak it lies in. An "all weights" item is stored under the base code.
  const spots = new Map();
  // Products that have at least one weight item: these never fall back to a base-code vak.
  const splitProducts = new Set();

  (entries || [])
    .filter((entry) => entry && entry.active !== false)
    .forEach((entry) => {
      const sequence = entry.sequence;
      if (typeof sequence !== "number") return;
      const spot = spotOf(entry);

      (entry.products || []).forEach((product) => {
        if (!product || !product.productId) return;
        const key = productKey(product.shopSource, product.productId);
        // First entry wins: with vakken per gewicht a product sits on several entries, and a line
        // that only matches on product (not on weight) should sort with the first of them.
        if (!byProduct.has(key)) byProduct.set(key, sequence);

        const weight = itemWeight(product);
        if (weight !== null) splitProducts.add(key);
        const code = articleCode(product.shopSource, product.productId, weight);
        if (!spots.has(code)) spots.set(code, spot);

        const normalized = normalizeProductName(product.name);
        // First entry wins: a duplicate name must not silently move an existing product
        if (!!normalized && !byName.has(normalized)) byName.set(normalized, sequence);
      });

      const normalizedLabel = normalizeProductName(entry.label);
      if (!!normalizedLabel && !byName.has(normalizedLabel)) byName.set(normalizedLabel, sequence);
    });

  return { byProduct, byName, spots, splitProducts, size: byProduct.size };
};

/**
 * The vak a product (optionally one weight of it) lies in, or null.
 *
 * Exact on article code first. Only a product that is NOT split per weight falls back to its
 * "all weights" vak - a split product with a weight that has no vak of its own must stay without
 * one, or the picker would be sent to the vak of a different weight with a barcode that scans as
 * correct. Never matches on name: two different products can share one.
 */
export const findRouteSpot = (index, shopSource, productId, weight) => {
  if (!index || !index.spots || !productId) return null;
  const normalised = normaliseWeight(weight);
  const exact = index.spots.get(articleCode(shopSource, productId, normalised));
  if (exact) return exact;
  if (normalised !== null && !index.splitProducts.has(productKey(shopSource, productId))) {
    return index.spots.get(articleBaseCode(shopSource, productId)) || null;
  }
  return null;
};

/**
 * Position of a single packing slip line, or null when it is not on the route yet.
 */
export const getPickSequence = (index, line) => {
  if (!index || !line) return null;

  if (!!line.productId) {
    // A pick line carries `weight`, a packing slip line `variation.weight`
    const weight = line.weight !== undefined ? line.weight : line.variation?.weight;
    const spot = findRouteSpot(index, line.shopSource, line.productId, weight);
    if (spot) return spot.sequence;
    const bySource = index.byProduct.get(productKey(line.shopSource, line.productId));
    if (bySource !== undefined) return bySource;
  }

  const normalized = normalizeProductName(line.name);
  const byName = index.byName.get(normalized);
  return byName === undefined ? null : byName;
};

/**
 * Order the lines of one packing slip along the walking route.
 *
 * Lines that are not on the route keep the picker walking: they are put after everything
 * that is, sorted by name, and flagged so the slip can mark them. Never dropped.
 * Returns new line objects with `pickSequence` and `offRoute` attached.
 */
export const sortLinesByPickRoute = (lines, index) => {
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "nl");

  const positioned = (lines || []).map((line) => {
    const pickSequence = getPickSequence(index, line);
    return { ...line, pickSequence, offRoute: pickSequence === null };
  });

  const known = positioned.filter((line) => !line.offRoute);
  const unknown = positioned.filter((line) => line.offRoute);

  known.sort((a, b) => (a.pickSequence !== b.pickSequence ? a.pickSequence - b.pickSequence : byName(a, b)));
  unknown.sort(byName);

  return [...known, ...unknown];
};

/**
 * Sorting used before there was a route, kept as the fallback while the list is still empty.
 * Nothing is off-route here: with no route configured there is nothing to be off, and the
 * packing slip is a customer-facing document that must not sprout markers on every line.
 */
export const sortLinesByPurchased = (lines) =>
  [...(lines || [])]
    .sort((a, b) => (b.purchased || 0) - (a.purchased || 0))
    .map((line) => ({ ...line, pickSequence: null, offRoute: false }));

/**
 * Sort one packing slip's lines: along the route when there is one, on `purchased` when
 * there is not. An empty route must never reorder anything, so the switch is explicit.
 */
export const sortPackingSlipLines = (lines, index) => {
  if (!index || index.size === 0) return sortLinesByPurchased(lines);
  return sortLinesByPickRoute(lines, index);
};

const shopProductsOf = (entry) => (entry && entry.products) || [];

/** Every `${shop}|${productId}` already placed somewhere on the route. */
const placedProductKeys = (entries) => {
  const placed = new Set();
  (entries || []).forEach((entry) =>
    shopProductsOf(entry).forEach((product) => {
      if (!!product && !!product.productId) placed.add(productKey(product.shopSource, product.productId));
    }),
  );
  return placed;
};

const toEntryProduct = (product) => ({
  shopSource: product.shopSource,
  productId: product.id,
  name: product.name || "",
});

/** Popularity of an entry, taken from VCE - the shop the freezer is actually laid out along. */
const vcePurchasedOf = (entry, purchasedByKey) => {
  const vce = shopProductsOf(entry).find((product) => product.shopSource === SHOP_VCE);
  if (!vce) return -1;
  return purchasedByKey.get(productKey(SHOP_VCE, vce.productId)) || 0;
};

/**
 * Build the route from scratch: VCE products ordered by how often they sell, with each HVO
 * product folded into the VCE entry of the same name. HVO-only products land at the end,
 * where they are easy to find and place by hand.
 *
 * Only used for the very first fill and for an explicit "reset order" - a rebuild throws
 * away manual positioning, which is the whole value of the list.
 */
export const buildRouteFromProducts = (products) => {
  const vceProducts = (products || []).filter((product) => product.shopSource === SHOP_VCE);
  const hvoProducts = (products || []).filter((product) => product.shopSource === SHOP_HVO);

  const entries = [...vceProducts]
    .sort((a, b) => (b.purchased || 0) - (a.purchased || 0))
    .map((product) => ({
      label: product.name || "",
      products: [toEntryProduct(product)],
      active: true,
    }));

  const entryByName = new Map();
  entries.forEach((entry) => {
    const normalized = normalizeProductName(entry.label);
    if (!!normalized && !entryByName.has(normalized)) entryByName.set(normalized, entry);
  });

  hvoProducts.forEach((product) => {
    const match = entryByName.get(normalizeProductName(product.name));
    if (match) {
      match.products.push(toEntryProduct(product));
      return;
    }
    entries.push({ label: product.name || "", products: [toEntryProduct(product)], active: true });
  });

  return withSequences(entries);
};

/** (Re)number a list so its array order becomes its route order. */
export const withSequences = (entries) =>
  (entries || []).map((entry, position) => ({ ...entry, sequence: (position + 1) * SEQUENCE_STEP }));

/** Vak codes in the order a person reads them: 1A, 1B, 2A, 10A - numbers as numbers. */
export const compareLocationCodes = (a, b) => String(a).localeCompare(String(b), "nl", { numeric: true, sensitivity: "base" });

/**
 * "Looproute op vakcode zetten": the vakken with a code first, in code order, then the spots
 * without one in the order they had. With one series of codes in walking order (stelling 1, 2, 3
 * along the aisle, both sides alike - planning/magazijn-inrichten.md) the code order is the
 * walking order, so this replaces the old order on popularity with the real one. A new order to
 * review and save, like a drag; nothing is written here.
 */
export const sortRouteByLocation = (entries) => {
  const list = entries || [];
  const coded = list.filter((entry) => !!entry.location).sort((a, b) => compareLocationCodes(a.location, b.location));
  return [...coded, ...list.filter((entry) => !entry.location)];
};

/**
 * Work out what a "update from products" run should change, without touching what is
 * already positioned. New products are attached to the entry of the same name when there is
 * one, and otherwise appended - so running this never costs someone their manual ordering.
 *
 * Returns the changes rather than applying them, so the page can show what is about to happen.
 */
export const buildRouteSync = (entries, products) => {
  const existing = entries || [];
  const placed = placedProductKeys(existing);

  const entryByName = new Map();
  existing.forEach((entry) => {
    const normalized = normalizeProductName(entry.label);
    if (!!normalized && !entryByName.has(normalized)) entryByName.set(normalized, entry);
    shopProductsOf(entry).forEach((product) => {
      const productNormalized = normalizeProductName(product.name);
      if (!!productNormalized && !entryByName.has(productNormalized)) entryByName.set(productNormalized, entry);
    });
  });

  const missing = (products || []).filter(
    (product) => !!product.id && !placed.has(productKey(product.shopSource, product.id)),
  );

  // Attaching to an existing entry, keyed by entry id so two new products can join the same spot
  const additionsByEntry = new Map();
  const created = [];

  let nextSequence =
    existing.reduce((highest, entry) => Math.max(highest, entry.sequence || 0), 0) + SEQUENCE_STEP;

  missing.forEach((product) => {
    const match = entryByName.get(normalizeProductName(product.name));
    if (match) {
      if (!!match.id) {
        const additions = additionsByEntry.get(match.id) || { entry: match, products: [] };
        additions.products.push(toEntryProduct(product));
        additionsByEntry.set(match.id, additions);
      } else {
        // An entry created earlier in this same run, so it is not in Firestore yet and has no
        // id to key an update on - the product just joins it before it gets written
        match.products.push(toEntryProduct(product));
      }
      return;
    }
    const entry = {
      label: product.name || "",
      products: [toEntryProduct(product)],
      active: true,
      sequence: nextSequence,
    };
    nextSequence += SEQUENCE_STEP;
    created.push(entry);
    // A VCE and an HVO product with the same new name should share the entry that was just made
    const normalized = normalizeProductName(entry.label);
    if (!!normalized) entryByName.set(normalized, entry);
  });

  return {
    created,
    attached: Array.from(additionsByEntry.values()),
    // Entries pointing at products that no longer exist. Reported, never deleted automatically:
    // a product can disappear from the webshop while its box is still in the freezer.
    orphaned: findOrphanedEntries(existing, products),
  };
};

/**
 * Whether an item points at a product that no longer exists. Only a shop whose catalogue loaded
 * can say so: a shop that failed to load looks empty, and all of its items would look gone.
 */
const goneItemTest = (products, loadedShops) => {
  const known = new Set(
    (products || []).filter((product) => !!product.id).map((product) => productKey(product.shopSource, product.id)),
  );
  const loaded = new Set(loadedShops || [SHOP_VCE, SHOP_HVO]);
  return (item) => loaded.has(item.shopSource) && !known.has(productKey(item.shopSource, item.productId));
};

/** Entries whose shop products have all disappeared from the products collections. */
export const findOrphanedEntries = (entries, products, { loadedShops } = {}) => {
  const gone = goneItemTest(products, loadedShops);
  return (entries || []).filter((entry) => {
    const entryProducts = shopProductsOf(entry);
    // An empty vak with a code is a physical spot waiting for a product, not a leftover
    if (entryProducts.length === 0) return !entry.location;
    return entryProducts.every(gone);
  });
};

/**
 * Taking products that no longer exist off the route in one go - the "Opruimen" of the orphans
 * warning, which is never done automatically (a product can leave the webshop while its box is
 * still in the freezer). Per entry:
 *
 *  - the items of products that are gone come off, the other items stay;
 *  - an entry left without items and without a vak code goes: nothing hangs in the freezer;
 *  - a vak with a code stays, empty: its label is on the rack and a product can move in;
 *  - an entry that already had no items and no code goes too (the other kind of leftover).
 *
 * @returns {{ updates: Array<{entry, products, removed}>, deletes: Array<{entry, removed}> }}
 *   `products`: what an updated entry keeps; `removed`: the items that come off.
 */
export const planOrphanCleanup = (entries, products, { loadedShops } = {}) => {
  const gone = goneItemTest(products, loadedShops);
  const updates = [];
  const deletes = [];
  (entries || []).forEach((entry) => {
    const items = shopProductsOf(entry);
    if (items.length === 0) {
      if (!entry.location) deletes.push({ entry, removed: [] });
      return;
    }
    const removed = items.filter(gone);
    if (removed.length === 0) return;
    const remaining = items.filter((item) => !gone(item));
    if (remaining.length === 0 && !entry.location) deletes.push({ entry, removed });
    else updates.push({ entry, products: remaining, removed });
  });
  return { updates, deletes };
};

/**
 * Renumber the existing entries by VCE popularity, keeping the entries themselves intact.
 * The destructive one: it undoes manual ordering, so the page asks first.
 */
export const buildRouteReset = (entries, products) => {
  const purchasedByKey = new Map(
    (products || [])
      .filter((product) => !!product.id)
      .map((product) => [productKey(product.shopSource, product.id), product.purchased || 0]),
  );

  const sorted = [...(entries || [])].sort((a, b) => {
    const difference = vcePurchasedOf(b, purchasedByKey) - vcePurchasedOf(a, purchasedByKey);
    if (difference !== 0) return difference;
    // numeric: "Kogelbiefstuk 125 gram" before "Kogelbiefstuk 1000 gram"
    return (a.label || "").localeCompare(b.label || "", "nl", { numeric: true });
  });

  return withSequences(sorted);
};

/**
 * Move one entry to a new position in the list and renumber. Used by both drag-and-drop and
 * the position input, so they can never disagree about what a position means.
 */
export const moveEntry = (entries, fromIndex, toIndex) => {
  const list = [...(entries || [])];
  if (fromIndex < 0 || fromIndex >= list.length) return list;
  const target = Math.max(0, Math.min(toIndex, list.length - 1));
  const [moved] = list.splice(fromIndex, 1);
  list.splice(target, 0, moved);
  return withSequences(list);
};

/** Shop products that are not on the route yet - what the page warns about. */
export const findUnplacedProducts = (entries, products) => {
  const placed = placedProductKeys(entries);
  return (products || []).filter(
    (product) => !!product.id && !placed.has(productKey(product.shopSource, product.id)),
  );
};

/**
 * The entry a shop product currently sits on, or null when it has no spot yet. With a weight
 * (null included) only the item for exactly that weight counts; without, any item of the product.
 */
export const findProductEntry = (entries, shopSource, productId, weight) =>
  (entries || []).find((entry) =>
    shopProductsOf(entry).some(
      (product) =>
        product.productId === productId &&
        product.shopSource === shopSource &&
        (weight === undefined || itemWeight(product) === normaliseWeight(weight)),
    ),
  ) || null;

const sameItem = (item, shopSource, productId, weight) =>
  item.shopSource === shopSource && item.productId === productId && itemWeight(item) === normaliseWeight(weight);

/** All items of one product anywhere on the route. */
const itemsOfProduct = (entries, shopSource, productId) =>
  (entries || []).flatMap((entry) =>
    shopProductsOf(entry).filter((product) => product.shopSource === shopSource && product.productId === productId),
  );

/** An entry's products without one exact item (shop, product and weight). */
export const removeEntryProduct = (entry, item) =>
  shopProductsOf(entry).filter((existing) => !sameItem(existing, item.shopSource, item.productId, item.weight));

/**
 * Work out the writes for putting a shop product on a given entry.
 *
 * Linking is a *move*, not an addition: when the two shops name the same product slightly
 * differently ("Kipfilet" vs "Kipfilet "), the route was built with the HVO product on its own
 * entry. Folding it into the VCE entry has to take it off the old one, or it would sit on two
 * spots at once and the packing slip would pick whichever came first.
 *
 * Returns null when there is nothing to do, so the caller can skip the write.
 */
export const planProductLink = (entries, targetEntry, product, options = {}) => {
  if (!targetEntry || !targetEntry.id || !product) return null;

  const shopSource = product.shopSource;
  // Accepts both a products-collection product (`id`) and an entry product (`productId`)
  const productId = product.id || product.productId;
  if (!productId) return null;
  // An entry item carries its own weight; a products-collection product has none (null = all weights)
  const weight = normaliseWeight(options.weight !== undefined ? options.weight : product.weight);

  // Per product: one "all weights" item, or weight items only - never both (besluit 2)
  const items = itemsOfProduct(entries, shopSource, productId);
  if (weight !== null && items.some((item) => itemWeight(item) === null)) return { refused: "pickRoute/split-first" };
  if (weight === null && items.some((item) => itemWeight(item) !== null)) return { refused: "pickRoute/already-split" };

  const source = findProductEntry(entries, shopSource, productId, weight);
  if (!!source && source.id === targetEntry.id) return null;

  const remaining = !source ? [] : removeEntryProduct(source, { shopSource, productId, weight });
  const item = { shopSource, productId, name: product.name || "" };
  if (weight !== null) item.weight = weight;

  return {
    target: targetEntry,
    targetProducts: [...shopProductsOf(targetEntry), item],
    source,
    sourceProducts: remaining,
    // An entry with no products left is not a spot in the freezer any more - it only exists
    // because the product was on it. Leaving it behind would clutter the route with empties.
    // Unless it has a vak code: then it is a physical vak that is simply empty for now.
    removeSource: !!source && remaining.length === 0 && !source.location,
  };
};

// --- vakken: codes, barcodes, empty vakken --------------------------------------------------

/** A vak code: letters and digits, at most six, e.g. "1A", "40H", "P03". */
export const LOCATION_CODE_PATTERN = /^[A-Z0-9]{1,6}$/;

/** What someone typed, as the code it is: no spaces, upper case. "" for nothing. */
export const normaliseLocationCode = (code) =>
  code === null || code === undefined ? "" : String(code).replace(/\s+/g, "").toUpperCase();

export const validateLocationCode = (code) => LOCATION_CODE_PATTERN.test(normaliseLocationCode(code));

export const findEntryByLocation = (entries, code) => {
  const normalised = normaliseLocationCode(code);
  if (!normalised) return null;
  return (entries || []).find((entry) => normaliseLocationCode(entry.location) === normalised) || null;
};

export const findEntryByBarcode = (entries, barcode) =>
  (entries || []).find((entry) => !!barcode && (entry.barcode || locationBarcode(entry.location)) === barcode) || null;

/**
 * The fields to write when a vak gets a code (or loses it), after checking the code is valid and
 * not used by another vak. Firestore cannot enforce uniqueness on auto-id documents, so this is
 * the check - the hook runs it again on a fresh read right before writing.
 */
export const planLocationAssignment = (entries, entryId, { location, zone, label } = {}) => {
  const entry = (entries || []).find((candidate) => candidate.id === entryId);
  if (!entry) return { ok: false, reason: "pickRoute/not-found" };

  const code = normaliseLocationCode(location);
  if (!!code && !LOCATION_CODE_PATTERN.test(code)) return { ok: false, reason: "pickRoute/invalid-location" };
  if (!!code) {
    const taken = (entries || []).find(
      (candidate) => candidate.id !== entryId && normaliseLocationCode(candidate.location) === code,
    );
    if (taken) return { ok: false, reason: "pickRoute/location-taken", takenBy: taken };
  }

  const updates = { location: code || null, barcode: code ? locationBarcode(code) : null };
  if (zone !== undefined) updates.zone = !zone ? null : String(zone).trim() || null;
  if (label !== undefined) updates.label = String(label || "").trim();
  return { ok: true, updates };
};

/** A vak without a product yet - the floor plan can be entered before the products are placed. */
export const buildEmptyEntry = ({ location, zone, label } = {}, entries) => {
  const code = normaliseLocationCode(location) || null;
  const last = (entries || []).reduce((highest, entry) => Math.max(highest, entry.sequence || 0), 0);
  return {
    label: String(label || "").trim(),
    location: code,
    barcode: locationBarcode(code),
    zone: !zone ? null : String(zone).trim() || null,
    products: [],
    active: true,
    sequence: last + SEQUENCE_STEP,
  };
};

/**
 * The code after this one, for numbering a rack in one go: "1A" -> "1B", and after the last
 * letter of a rack the next rack ("1H" -> "2A" with eight vakken per rack). A code that ends in a
 * number counts up ("P03" -> "P04"). null when the code has neither shape.
 */
export const nextLocationCode = (code, { lettersPerRack = 8 } = {}) => {
  const normalised = normaliseLocationCode(code);
  let match = /^(\d+)([A-Z])$/.exec(normalised);
  if (match) {
    const letter = match[2].charCodeAt(0) - 65;
    // A number for every stelling alike, or per stelling number (rackLayout below)
    const letters = typeof lettersPerRack === "function" ? lettersPerRack(Number(match[1])) : lettersPerRack;
    if (letter + 1 < letters) return `${match[1]}${String.fromCharCode(66 + letter)}`;
    return `${Number(match[1]) + 1}A`;
  }
  match = /^([A-Z]*)(\d+)$/.exec(normalised);
  if (match) return `${match[1]}${String(Number(match[2]) + 1).padStart(match[2].length, "0")}`;
  return null;
};

/**
 * "Vakken doornummeren": give the entries codes in route order, starting at `startCode`, skipping
 * codes another vak already has. With `onlyEmpty` (the default) entries that already have a code
 * keep it. Returns the changes instead of writing them, so the screen can show them first.
 */
/**
 * Vakken per stelling from a rack layout (parseRackLayout): layout[0] is the stelling of the start
 * code, [1] the next one, and the last number holds for the rest. For nextLocationCode.
 */
export const rackLetters = (startCode, layout, fallback = 8) => {
  const firstRack = Number((/^(\d+)[A-Z]$/.exec(normaliseLocationCode(startCode)) || [])[1]);
  if (!layout || layout.length === 0 || !Number.isFinite(firstRack)) return fallback;
  return (rack) => layout[Math.min(Math.max(rack - firstRack, 0), layout.length - 1)];
};

export const assignLocationsSequentially = (entries, { startCode, lettersPerRack = 8, rackLayout = null, onlyEmpty = true } = {}) => {
  let code = normaliseLocationCode(startCode);
  if (!LOCATION_CODE_PATTERN.test(code)) return { ok: false, reason: "pickRoute/invalid-location", changes: [] };
  lettersPerRack = rackLetters(code, rackLayout, lettersPerRack);

  const list = entries || [];
  const inUse = new Set(
    onlyEmpty ? list.filter((entry) => !!entry.location).map((entry) => normaliseLocationCode(entry.location)) : [],
  );
  const changes = [];
  let unassigned = 0;

  list.forEach((entry) => {
    if (onlyEmpty && entry.location) return;
    let guard = 0;
    while (code && inUse.has(code) && guard < 10000) {
      code = nextLocationCode(code, { lettersPerRack });
      guard += 1;
    }
    if (!code || !LOCATION_CODE_PATTERN.test(code)) {
      unassigned += 1;
      return;
    }
    changes.push({ entryId: entry.id, location: code, barcode: locationBarcode(code) });
    inUse.add(code);
    code = nextLocationCode(code, { lettersPerRack });
  });

  return { ok: true, changes, unassigned };
};

/**
 * "Vakken per stelling" as typed: one number for all stellingen ("8"), or one per stelling in route
 * order ("8, 8, 6"), with repeats ("3x8, 2x6"). The warehouse has baskets racks of 8 (4 high, 2
 * wide) and of 6 (3 high, 2 wide). The last number holds for the stellingen after it.
 *
 * @returns {{ ok: true, layout: number[] } | { ok: false }}
 */
export const parseRackLayout = (text) => {
  const tokens = String(text || "")
    .split(/[\s,;]+/)
    .filter(Boolean);
  if (tokens.length === 0) return { ok: false };
  const layout = [];
  for (const token of tokens) {
    const match = /^(?:(\d+)\s*[x×*])?(\d+)$/i.exec(token);
    if (!match) return { ok: false };
    const times = match[1] === undefined ? 1 : Number(match[1]);
    const letters = Number(match[2]);
    if (times < 1 || times > 500 || letters < 1 || letters > 26) return { ok: false };
    for (let index = 0; index < times; index += 1) layout.push(letters);
  }
  return { ok: true, layout };
};

// --- vakken per gewicht ---------------------------------------------------------------------

const weightsInCatalogue = (catalogue, shopSource, productId) =>
  ((catalogue && catalogue.get(productKey(shopSource, productId))?.articles) || [])
    .map((article) => article.weight)
    .filter((weight) => weight !== null);

/** "Kogelbiefstuk" + "250" -> "Kogelbiefstuk 250 gram", unless the label already says so. */
export const withWeightLabel = (label, weight) => {
  const base = String(label || "").trim();
  if (weight === null || weight === undefined) return base;
  if (base.includes(`${weight} gram`) || base.endsWith(` ${weight}`)) return base;
  return `${base} ${weight} gram`.trim();
};

// --- bordje en inhoud -------------------------------------------------------------------------

const WEIGHT_IN_TEXT = /(\d+(?:[.,]\d+)?)\s*(kilo|kg|gram|gr|g)\b/gi;
const compactName = (name) => normalizeProductName(name).replace(/\s+/g, "");

/** The weights a text names, in grams: "250 gram" -> "250", "1 kilo" and "1,5 kg" too. */
const weightsNamedIn = (text) =>
  [...String(text || "").matchAll(WEIGHT_IN_TEXT)].map(([, amount, unit]) => {
    const grams = Number(amount.replace(",", ".")) * (/^k/i.test(unit) ? 1000 : 1);
    return normaliseWeight(String(Math.round(grams)));
  });

/** The bordje text the CMS writes for what lies in a vak: the VCE article's name and weight. */
export const expectedLabelOf = (entry) => {
  const items = shopProductsOf(entry);
  const first = items.find((item) => item.shopSource === SHOP_VCE) || items[0];
  return first ? withWeightLabel(first.name, itemWeight(first)) : "";
};

/**
 * Whether a vak's bordje text says something else than what lies in it (Sam, 2026-10-22): the
 * bordje says "kogelbiefstuk 150 gram", the vak holds the 125 gram. The text has to name the
 * product, and a weight it names has to be one that lies there; "Kogelbiefstuk" alone or
 * "Kogelbiefstuk 2x125" fits. A weight inside the product's own name ("Kalfsworstjes (500 gram)")
 * does not count. An empty vak, or one without bordje text, has nothing to disagree with.
 *
 * @returns {string|null} the text that does fit, or null when the bordje is right
 */
export const labelMismatch = (entry) => {
  const items = shopProductsOf(entry);
  const label = String((entry && entry.label) || "").trim();
  if (items.length === 0 || !label) return null;

  const names = [...new Set(items.map((item) => String(item.name || "").trim()).filter(Boolean))];
  const labelName = compactName(label);
  const nameFits = names.length === 0 || names.some((name) => labelName.includes(compactName(name)) || compactName(name).includes(labelName));

  const lying = new Set(items.map(itemWeight).filter((weight) => weight !== null));
  // The longest name first, so "Kipfilet blokjes" goes before "Kipfilet"
  const rest = names
    .sort((a, b) => b.length - a.length)
    .reduce((text, name) => text.split(name.toLowerCase()).join(" "), label.toLowerCase());
  const weightFits = lying.size === 0 || weightsNamedIn(rest).every((weight) => lying.has(weight));

  return nameFits && weightFits ? null : expectedLabelOf(entry);
};

/** Every vak whose bordje text does not fit what lies in it, with the text that does. */
export const findLabelMismatches = (entries) =>
  (entries || []).map((entry) => ({ entry, expected: labelMismatch(entry) })).filter((mismatch) => mismatch.expected !== null);

/** Entries that still hold an "all weights" item for a product that is sold in weights. */
export const entriesNeedingSplit = (entries, catalogue) =>
  (entries || []).filter((entry) =>
    shopProductsOf(entry).some(
      (item) => itemWeight(item) === null && weightsInCatalogue(catalogue, item.shopSource, item.productId).length > 0,
    ),
  );

/**
 * Articles that need a vak and have none: weights of a product that is already split per weight
 * but not for this weight (a new variant, or a weight only a package asks for). A product with
 * one "all weights" vak is covered; a product with no vak at all is reported by
 * findUnplacedProducts instead, so nothing is counted twice.
 */
export const findUnplacedArticles = (entries, catalogue) => {
  const placedWeights = new Map();
  (entries || []).forEach((entry) =>
    shopProductsOf(entry).forEach((item) => {
      const key = productKey(item.shopSource, item.productId);
      if (!placedWeights.has(key)) placedWeights.set(key, new Set());
      placedWeights.get(key).add(itemWeight(item));
    }),
  );
  const missing = [];
  (catalogue || new Map()).forEach((product, key) => {
    const placed = placedWeights.get(key);
    if (!placed || placed.has(null)) return;
    product.articles.forEach((article) => {
      if (article.weight !== null && !placed.has(article.weight)) {
        missing.push({ key, shopSource: product.shopSource, productId: product.productId, name: product.name, ...article });
      }
    });
  });
  return missing;
};

/**
 * Plan "Vakken per gewicht maken" (besluit 2): turn every "all weights" item of a product that is
 * sold in weights into one item per weight, each in its own vak.
 *
 * Per entry that holds such an item:
 *  - its weights (from the article catalogue, VCE and HVO together) are handled smallest first;
 *  - a weight goes to the vak that already holds a product of the same entry at that weight - so
 *    VCE Kogelbiefstuk 250 g and HVO Kogelbiefstuk 250 g end up in one vak, also when one of the
 *    two was split earlier;
 *  - otherwise the first weight stays on the entry itself (keeping its vak code) and every
 *    further weight becomes a new entry directly behind it, labelled with the weight.
 * Weights of an already split product that have no vak yet (a new variant) are placed the same
 * way, behind the product's last vak. Positions are renumbered so the new vakken sit in route
 * order; nothing already placed moves elsewhere.
 *
 * Returns `{ ordered, creates, updates, summary }` without writing anything. `creates` carry a
 * `tempKey` the hook swaps for a real document id.
 */
export const planWeightSplit = (entries, catalogue) => {
  const originals = new Map((entries || []).map((entry) => [entry.id, entry]));
  const list = (entries || []).map((entry) => ({ ...entry, products: [...shopProductsOf(entry)] }));
  const created = new Set();
  const touched = new Set();
  let tempCounter = 0;
  let placedArticles = 0;
  let splitEntries = 0;

  const holds = (entry, shopSource, productId, weight) =>
    entry.products.some((item) => sameItem(item, shopSource, productId, weight));
  const findWeightEntry = (siblingKeys, weight) =>
    list.find((entry) =>
      entry.products.some(
        (item) => siblingKeys.has(productKey(item.shopSource, item.productId)) && itemWeight(item) === weight,
      ),
    ) || null;
  const createAfter = (anchor, label) => {
    tempCounter += 1;
    const entry = { tempKey: `new-${tempCounter}`, label, products: [], active: true };
    list.splice(list.indexOf(anchor) + 1, 0, entry);
    created.add(entry);
    return entry;
  };
  const place = (target, item, weight) => {
    if (holds(target, item.shopSource, item.productId, weight)) return;
    target.products.push({ shopSource: item.shopSource, productId: item.productId, name: item.name || "", weight });
    touched.add(target);
    placedArticles += 1;
  };

  // 1. "all weights" items of products that are sold in weights
  [...list].forEach((entry) => {
    const catchAlls = entry.products.filter(
      (item) => itemWeight(item) === null && weightsInCatalogue(catalogue, item.shopSource, item.productId).length > 0,
    );
    if (catchAlls.length === 0) return;
    splitEntries += 1;

    const siblingKeys = new Set(entry.products.map((item) => productKey(item.shopSource, item.productId)));
    const baseLabel = entry.label || catchAlls[0].name || "";
    let claimed = entry.products.some((item) => itemWeight(item) !== null);
    let tail = entry;
    entry.products = entry.products.filter((item) => !catchAlls.includes(item));
    touched.add(entry);

    const weights = [...new Set(catchAlls.flatMap((item) => weightsInCatalogue(catalogue, item.shopSource, item.productId)))].sort(
      compareWeights,
    );
    weights.forEach((weight) => {
      let target = findWeightEntry(siblingKeys, weight);
      if (!target && !claimed) {
        target = entry;
        claimed = true;
        entry.label = withWeightLabel(baseLabel, weight);
      }
      if (!target) {
        target = createAfter(tail, withWeightLabel(baseLabel, weight));
        tail = target;
      }
      catchAlls
        .filter((item) => weightsInCatalogue(catalogue, item.shopSource, item.productId).includes(weight))
        .forEach((item) => place(target, item, weight));
    });
  });

  // 2. weights of already split products that have no vak yet
  findUnplacedArticles(list, catalogue).forEach((article) => {
    const productEntries = list.filter((entry) =>
      entry.products.some((item) => item.shopSource === article.shopSource && item.productId === article.productId),
    );
    const siblingKeys = new Set(productEntries.flatMap((entry) => entry.products.map((item) => productKey(item.shopSource, item.productId))));
    const item = { shopSource: article.shopSource, productId: article.productId, name: article.name };
    let target = findWeightEntry(siblingKeys, article.weight);
    if (!target) {
      const anchor = productEntries[productEntries.length - 1];
      const baseLabel = (anchor && anchor.products.find((candidate) => candidate.productId === article.productId)?.name) || article.name;
      target = createAfter(anchor, withWeightLabel(baseLabel, article.weight));
    }
    place(target, item, article.weight);
  });

  // Only new vakken need room in the route; without them every position stays exactly as it was
  const ordered = created.size > 0 ? withSequences(list) : list.map((entry) => ({ ...entry }));
  const creates = [];
  const updates = [];
  ordered.forEach((entry, index) => {
    const working = list[index];
    if (created.has(working)) {
      creates.push({ tempKey: working.tempKey, data: { label: entry.label, products: entry.products, active: true, sequence: entry.sequence } });
      return;
    }
    const original = originals.get(entry.id);
    const data = {};
    if (touched.has(working) && JSON.stringify(entry.products) !== JSON.stringify(shopProductsOf(original))) data.products = entry.products;
    if (touched.has(working) && entry.label !== original.label) data.label = entry.label;
    if (entry.sequence !== original.sequence) data.sequence = entry.sequence;
    if (Object.keys(data).length > 0) updates.push({ id: entry.id, data });
  });

  return {
    ordered,
    creates,
    updates,
    summary: { splitEntries, createdEntries: creates.length, placedArticles },
  };
};

// --- readiness of the warehouse data (fase 0) ------------------------------------------------

const hasProductWeight = (product) =>
  (product.variations || []).some((variation) => normaliseWeight(variation?.weight) !== null) ||
  (product.pickWeightGram !== null && product.pickWeightGram !== undefined && normaliseWeight(product.pickWeightGram) !== null);

/**
 * Products that can end up in a vak: everything except packages (picked as their contents) and
 * gift cards (not picked at all). What the route should be generated and checked against.
 */
export const pickableProducts = (products) =>
  (products || []).filter(
    (product) => !!product && !(Array.isArray(product.productItems) && product.productItems.length > 0) && !product.giftCard,
  );

/** Entries holding only package products: left over from before, they never get picked from. */
export const findPackageEntries = (entries, products) => {
  const packages = new Set(
    (products || [])
      .filter((product) => Array.isArray(product.productItems) && product.productItems.length > 0)
      .map((product) => productKey(product.shopSource, product.id)),
  );
  return (entries || []).filter((entry) => {
    const items = shopProductsOf(entry);
    return items.length > 0 && items.every((item) => packages.has(productKey(item.shopSource, item.productId)));
  });
};

/**
 * The checklist on the vakken screen: what still has to be done before a round can be picked
 * without surprises. Each count is a different job for the client, so they are kept apart.
 */
export const summariseRouteReadiness = ({ entries, products, catalogue }) => {
  const packageEntryIds = new Set(findPackageEntries(entries, products).map((entry) => entry.id));
  const vakEntries = (entries || []).filter((entry) => !packageEntryIds.has(entry.id));
  const pickable = pickableProducts(products);
  return {
    vakken: vakEntries.length,
    withoutLocation: vakEntries.filter((entry) => !entry.location).length,
    needingSplit: entriesNeedingSplit(vakEntries, catalogue).length,
    unplacedArticles: findUnplacedArticles(vakEntries, catalogue).length,
    unplacedProducts: findUnplacedProducts(vakEntries, pickable).length,
    productsWithoutWeight: pickable.filter((product) => !hasProductWeight(product)).length,
    labelMismatches: findLabelMismatches(vakEntries).length,
    packageEntries: packageEntryIds.size,
  };
};
